'use client';
import { PuzzleResultsState } from '@wt/app/game/_components/types';
import { usePuzzleTodays } from '@wt/app/game/_hooks/usePuzzleTodays';
import { getGameScore } from '@wt/app/game/_utils/getGameScore';
import ImageModal from '@wt/game/modals/ImageModal';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import useSettingsContext from '@wt/game/providers/settings/types';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';
import { getDayString } from '@wt/game/utils/getDayString';
import { GameMode } from '@wt/game/utils/types';
import { SidebarAd } from '@wt/shared/components/ads/SidebarAd';
import {
  Coordinate,
  PuzzleLocation,
} from '@wt/shared/components/maps/mapkit/types';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import { refreshSidebarAd } from '@wt/shared/hooks/useAds/useAds';
import useAnimatedRouter from '@wt/shared/hooks/useAnimatedRouter';
import useReactive from '@wt/shared/hooks/useReactive';
import { MapInteractionEvent } from '@wt/shared/libs/mapkit';
import { saveCommunityStarted } from '@wt/utilities/database';
import { getSiteMode } from '@wt/utilities/siteMode';
import { useMount } from 'ahooks';
import { isEqual } from 'lodash-es';
import { redirect } from 'next/navigation';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { getCountryFromCoords } from '../../_utils/getCountryFromCoords';
import { RoundGameDesktop } from './RoundGameDesktop';
import { RoundGameMobile } from './RoundGameMobile';
import HintWalletModal from '@wt/game/modals/HintWallet';
import { useHints } from '@wt/game/providers/hints/HintsProvider';
import { useResponsive } from '@wt/shared/hooks/useResponsive';
import { loadDailyCompleted } from '@wt/app/archive/utils';
import { getCookie, setCookie } from '@wt/utilities/cookies';

const RoundGameContainer = ({
  results,
  puzzleId,
  loaded,
  gameMode,
}: {
  results: PuzzleResultsState;
  loaded: boolean;
  puzzleId?: string;
  gameMode: GameMode;
}) => {
  const { animatedRoute, prefetchRoute } = useAnimatedRouter();
  const [todays, addGuess] = usePuzzleTodays(
    puzzleId ?? results.date,
    results.puzzle.length,
    gameMode
  );
  const hints = useHints();
  const [showResultsFor, setShowResultsFor] = useState<number>(-1);
  const {
    setNumRoundsCompleted,
    setCurrentRound,
    inProgressGuesses,
    setInProgressGuesses,
  } = useRoundProgress();
  const { loggedIn, setShowLoginToolip } = useUserProvider();

  useEffect(() => {
    if (results.round !== 0) return;
    if (gameMode !== 'daily') return;
    if (loggedIn) return;
    if (getCookie('isLoggedIn') === 'true') {
      setCookie('isLoggedIn', '', -1);
      setShowLoginToolip(true);
      return;
    }
    const dailyGamesCompleted = loadDailyCompleted();
    if (dailyGamesCompleted.length == 0) return;
    try {
      const storedHideToolip = localStorage.getItem('hide-login-tooltip');
      const hideTooltip: boolean =
        storedHideToolip != null ? JSON.parse(storedHideToolip) : false;
      if (hideTooltip) return;

      setShowLoginToolip(true);
    } catch (e) {
      return;
    }
  }, [results.round, gameMode, loggedIn, setShowLoginToolip]);

  const updateGuesses = () => {
    if (todays && !todays.guesses) {
      setNumRoundsCompleted && setNumRoundsCompleted(0);
      setCurrentRound && setCurrentRound(0);
    } else if (todays.guesses !== undefined) {
      setNumRoundsCompleted && setNumRoundsCompleted(todays.guesses.length);
      setCurrentRound && setCurrentRound(todays.guesses.length + 1);

      if (gameMode == 'community' && todays.guesses.length == 0) {
        saveCommunityStarted({ slug: puzzleId ?? '' });
      }
    }
  };

  let initialUpdate = false;
  useEffect(() => {
    // Only updateGuesses after todays.guesses is first loaded
    if (initialUpdate) return;
    if (!initialUpdate && todays.guesses) {
      initialUpdate = true;
    }
    updateGuesses();
  }, [todays.guesses]);

  const [year, setYear] = useState<number | null>(
    inProgressGuesses[results.round]?.year ?? null
  );

  const [selectedLocation, setSelectedLocation] = useState<
    Coordinate | undefined
  >(inProgressGuesses[results.round]?.location ?? undefined);
  const [selectedLocationSecondary, setSelectedLocationSecondary] = useState<
    Coordinate | undefined
  >(inProgressGuesses[results.round]?.locationSecondary ?? undefined);

  const [guessLocationCountry, setGuessLocationCountry] = useState<
    string | undefined
  >(undefined);
  const [guessLocationSecondaryCountry, setGuessLocationSecondaryCountry] =
    useState<string | undefined>(undefined);
  const [puzzleLocationCountry, setPuzzleLocationCountry] = useState<
    string | undefined
  >(undefined);

  const responsiveInfo = useResponsive();

  const handleLocation = (e: MapInteractionEvent | mapkit.Coordinate) => {
    if (e instanceof mapkit.Coordinate) {
      setSelectedLocation(e);
      return;
    }
    const coords = e.toCoordinates();
    if (coords && coords instanceof mapkit.Coordinate) {
      getCountryFromCoords(coords, setGuessLocationCountry);
      if (!puzzleLocationCountry) {
        getCountryFromCoords(
          new mapkit.Coordinate(
            results.puzzle[results.round].latitude,
            results.puzzle[results.round].longitude
          ),
          setPuzzleLocationCountry
        );
      }

      setSelectedLocation(coords);
    }
  };
  const handleLocationSecondary = (
    e: MapInteractionEvent | mapkit.Coordinate
  ) => {
    if (e instanceof mapkit.Coordinate) {
      setSelectedLocationSecondary(e);
      return;
    }
    const coords = e.toCoordinates();
    if (coords && coords instanceof mapkit.Coordinate) {
      getCountryFromCoords(coords, setGuessLocationSecondaryCountry);
      if (!puzzleLocationCountry) {
        getCountryFromCoords(
          new mapkit.Coordinate(
            results.puzzle[results.round].latitude,
            results.puzzle[results.round].longitude
          ),
          setPuzzleLocationCountry
        );
      }

      setSelectedLocationSecondary(coords);
    }
  };

  const handleYearChange = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    if (target) {
      setYear(parseInt(target.value));
    }
  };

  // set in progress guesses in round progress provider
  useEffect(() => {
    const newGuesses = [...inProgressGuesses];
    newGuesses.splice(results.round, 1, {
      year: inProgressGuesses[results.round].year,
      location: selectedLocation,
      locationSecondary: inProgressGuesses[results.round].locationSecondary,
    });
    setInProgressGuesses(newGuesses);
  }, [selectedLocation]);

  useEffect(() => {
    const newGuesses = [...inProgressGuesses];
    newGuesses.splice(results.round, 1, {
      year: inProgressGuesses[results.round].year,
      location: inProgressGuesses[results.round].location,
      locationSecondary: selectedLocationSecondary,
    });
    setInProgressGuesses(newGuesses);
  }, [selectedLocationSecondary]);

  useEffect(() => {
    const newGuesses = [...inProgressGuesses];
    newGuesses.splice(results.round, 1, {
      year: year,
      location: inProgressGuesses[results.round].location,
      locationSecondary: inProgressGuesses[results.round].locationSecondary,
    });
    setInProgressGuesses(newGuesses);
  }, [year]);

  useEffect(() => {
    gameMode != 'daily' && puzzleId
      ? prefetchRoute(`/${gameMode}/game/${puzzleId}/results`)
      : prefetchRoute('/game/results');
  }, [puzzleId, gameMode]);

  function handleGuess() {
    const yearRequired = getSiteMode() != 'wheretaken';
    if (results && selectedLocation && (year || !yearRequired)) {
      addGuess({
        puzzle: results.puzzle[results.round],
        location: selectedLocation,
        locationSecondary: selectedLocationSecondary,
        year: year ?? 0,
        correctCountry:
          (guessLocationCountry !== undefined &&
            guessLocationCountry === puzzleLocationCountry) ||
          (guessLocationSecondaryCountry !== undefined &&
            guessLocationSecondaryCountry === puzzleLocationCountry),
        hintsUsed:
          hints.getUsedHints().length > 0 ? hints.getUsedHints() : undefined,
      });
      setShowResultsFor(results.round);

      setTimeout(() => {
        updateGuesses();
        gameMode != 'daily' && puzzleId
          ? animatedRoute(`/${gameMode}/game/${puzzleId}/results`)
          : animatedRoute('/game/results');
      }, 800);
    }
  }

  const containerClass = `grow ${
    showResultsFor >= 0 ? 'pointer-events-none grayscale animate-pulse' : ''
  }`;

  const innerResults = {
    ...results,
    round: showResultsFor >= 0 ? showResultsFor : results.round,
  };

  const roundGameProps = {
    results: innerResults,
    handleGuess,
    selectedLocation,
    selectedLocationSecondary,
    handleLocation,
    handleLocationSecondary,
    year,
    handleYearChange,
  };

  return (
    <div className="flex size-full flex-col">
      <div className={containerClass}>
        {!responsiveInfo.lg && (
          <div className="flex size-full flex-col">
            <RoundGameMobile {...roundGameProps} />
          </div>
        )}
        {responsiveInfo.lg && (
          <div className="flex h-full ipad:flex-col-reverse desktop:flex-row">
            <SidebarAd />
            <RoundGameDesktop {...roundGameProps} />
          </div>
        )}
      </div>
      {loaded && (
        <>
          <ImageModal
            id="puzzle-image-modal"
            src={results.puzzle[results.round].url}
            alt={''}
          />
          <HintWalletModal mode="game" />
        </>
      )}
    </div>
  );
};

const RoundGame = ({
  puzzle,
  puzzleId,
  gameMode,
}: {
  puzzle: PuzzleLocation[];
  puzzleId: string;
  gameMode: GameMode;
}) => {
  const state: PuzzleResultsState = useReactive({
    date: puzzleId ?? getDayString(0),
    guesses: undefined,
    device: '',
    score: 0,
    blurUrl: '',
    round: 0,
    puzzle: puzzle,
  });

  useMount(() => {
    state.puzzle = puzzle;
  });
  const [todays] = usePuzzleTodays(
    puzzleId ?? state.date,
    puzzle.length,
    gameMode
  );
  const [settings] = useSettingsContext();
  const suffix = useMemo(
    () =>
      puzzleId && gameMode != 'daily'
        ? `/${gameMode}/game/${puzzleId}`
        : '/game',
    [puzzleId]
  );
  const [loaded, setLoaded] = useState<boolean>(false);
  const { setScore, setViewingResults, setPuzzleId } = useRoundProgress();

  useEffect(() => {
    if (todays && todays.guesses && settings && !loaded) {
      if (!isEqual(state.guesses, todays.guesses)) {
        state.guesses = todays.guesses;
        state.score =
          todays.guesses &&
          todays.guesses.reduce((c, pg) => {
            const ps = getGameScore(pg, settings?.distanceUnit);
            return c + ps.score.total;
          }, 0);
        setScore && setScore(state.score);
        state.round = todays.guesses.length;
        setLoaded(true);
      }
    }
  }, [todays, settings]);

  if (loaded && state.round === state.puzzle.length) {
    redirect(suffix + '/results');
  }

  useEffect(() => {
    refreshSidebarAd();
    setViewingResults && setViewingResults(false);
    setPuzzleId && setPuzzleId(puzzleId);
  }, []);

  return (
    <div className="relative flex size-full flex-col items-center justify-center">
      {loaded && state.round < state.puzzle.length ? (
        <RoundGameContainer
          results={state}
          puzzleId={puzzleId}
          loaded={loaded}
          gameMode={gameMode}
        />
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export default RoundGame;
